// # Assignments Component for Step 2 Mentorship
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { Paper, Typography, Box, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import './assignments2.css';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import earIcon from '../../../general/library/step1/step1_icons/ear.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import healthcareIcon from '../../../general/library/step1/step1_icons/healthcare.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../../../general/library/step1/step1_icons/newborn.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import qualityIcon from '../../../general/library/step1/step1_icons/quality.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import statisticsIcon from '../../../general/library/step1/step1_icons/statistics.png';
import surgeryIcon from '../../../general/library/step1/step1_icons/surgery.png';
import emergencyIcon from '../../../general/library/step1/step1_icons/emergency.png';
import wellPatientIcon from '../../../general/library/step1/step1_icons/well_patient.png';

function Assignments2({ isAdmin }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Define chapters for Step 2
  const chapters = [
    { id: 'Cardiovascular', title: 'Cardiovascular Medicine', icon: heartIcon },
    { id: 'Respiratory-ENT', title: 'Respiratory Medicine & ENT', icon: lungsIcon },
    { id: 'Renal-Urology', title: 'Renal Medicine & Urology', icon: kidneyIcon },
    { id: 'Endocrine', title: 'Endocrine Medicine', icon: endocrineIcon },
    { id: 'Hematology-Oncology', title: 'Hematology & Clinical Oncology', icon: bloodIcon },
    { id: 'Immunology', title: 'Clinical Immunology', icon: immunologyIcon },
    { id: 'Gastrointestinal', title: 'Gastrointestinal Medicine', icon: gitIcon },
    { id: 'Musculoskeletal', title: 'Musculoskeletal Medicine', icon: musculoskeletalIcon },
    { id: 'Neurology', title: 'Clinical Neurology', icon: cnsIcon },
    { id: 'Ophthalmology', title: 'Clinical Ophthalmology', icon: eyeIcon },
    { id: 'Psychiatry', title: 'Clinical Psychiatry', icon: psychiatryIcon },
    { id: 'Dermatology', title: 'Clinical Dermatology', icon: skinIcon },
    { id: 'Obstetrics-Gynecology', title: 'Obstetrics & Gynecology', icon: reproductiveIcon },
    { id: 'Pediatrics-Neonatology', title: 'Pediatrics & Neonatal Medicine', icon: newbornIcon },
    { id: 'Genetics', title: 'Medical Genetics', icon: biochemistryIcon },
    { id: 'Infectious-Disease', title: 'Infectious Disease Medicine', icon: infectiousIcon },
    { id: 'Surgery-Anesthesia', title: 'General Surgery & Anesthesia', icon: surgeryIcon },
    { id: 'Emergency-Medicine', title: 'Emergency Medicine & Trauma', icon: emergencyIcon },
    { id: 'Well-Patient', title: 'Preventive Medicine', icon: wellPatientIcon },
    { id: 'Biostatistics', title: 'Biostatistics & Epidemiology', icon: statisticsIcon },
    { id: 'Ethics-Communication', title: 'Medical Ethics & Communication', icon: publicHealthIcon },
    { id: 'Healthcare-Delivery', title: 'Healthcare Systems', icon: healthcareIcon },
    { id: 'Quality-Safety', title: 'Patient Safety & Quality Care', icon: qualityIcon },
    { id: 'Pharmacology-Review', title: 'Clinical Pharmacology', icon: pharmaIcon }
  ];

  return (
    <div className="assignments2_container">
      <SharedHeader />
      <div className="assignments2_main">
        <div className="assignments2_content">
          <Typography variant="h4" component="h1" className="assignments2_title">
            Step 2 Assignments {isMentor && <span className="assignments2_mentor_pill">Mentor</span>}
          </Typography>

          <Paper elevation={3} className="assignments2_section">
            <Box className="assignments2_header">
              <Typography variant="h6" component="h2">
                Chapters
              </Typography>
            </Box>

            <div className="assignments2_chapters_grid">
              {chapters.map(chapter => (
                <Tooltip 
                  key={chapter.id}
                  title="Coming Soon"
                  placement="top"
                  arrow
                >
                  <div className="assignments2_chapter_card assignments2_chapter_card_disabled">
                    <div className="assignments2_chapter_icon_wrapper">
                      <img src={chapter.icon} alt={chapter.title} />
                      <div className="assignments2_lock_icon">
                        <LockIcon />
                      </div>
                    </div>
                    <h3>{chapter.title}</h3>
                    <div className="assignments2_coming_soon_badge">Coming Soon</div>
                  </div>
                </Tooltip>
              ))}
            </div>
          </Paper>
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
}

export default Assignments2;
